/**
 * GamesOnline site behaviour: hub tabs, game stage + library, catalog filters,
 * FAQ/guides interactions and the standalone play page.
 */

(function () {
  'use strict';

  const isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let isGamePaused = false;
  let currentGameId = 'nightfall';
  let nightfallStarted = false;

  function hideStageStart() {
    document.querySelectorAll('.stage-start').forEach((el) => {
      el.hidden = true;
    });
  }

  let nightfallLoadingPromise = null;
  function loadNightfall() {
    if (nightfallLoadingPromise) return nightfallLoadingPromise;
    return (nightfallLoadingPromise = import('@game/styles.css').then(() => import('@game/main.ts')));
  }
  window.__gamesonlineLoadNightfall = loadNightfall;

  function startNightfall() {
    hideStageStart();
    if (nightfallStarted) return;
    nightfallStarted = true;
    loadNightfall();
  }

  // Below-the-fold images marked data-src load when they come within 400px of the viewport
  // (a tighter margin than native lazy-loading, so nothing competes with the first screen).
  function initDeferredImages() {
    const imgs = document.querySelectorAll('img[data-src]');
    if (!imgs.length) return;
    const swap = (img) => {
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
    };
    if (!('IntersectionObserver' in window)) {
      imgs.forEach(swap);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          io.unobserve(entry.target);
          swap(entry.target);
        });
      },
      { rootMargin: '400px 0px' },
    );
    imgs.forEach((img) => io.observe(img));
  }

  document.addEventListener('DOMContentLoaded', () => {
    initDeferredImages();
    initIntroSequence();
    initTabNavigation();
    initGameStageAndHUD();
    initLibraryCards();
    initCatalogAndShelves();
    initFaqAccordionAndSearch();
    initCard3DTilt();
    initKeyboardShortcuts();
    initStandalonePlayPage();
  });

  /* ==========================================================================
     1. First-Load Cinematic Intro Sequence (Section 7.1)
     ========================================================================== */
  function initIntroSequence() {
    const header = document.getElementById('site-header');
    const stageFrame = document.getElementById('frame');
    if (header) header.classList.add('is-ready');
    if (stageFrame) stageFrame.classList.add('is-revealed');
  }

  /* ==========================================================================
     2. Tab Navigation & Sliding Indicator (Section 2 & 7.2)
     ========================================================================== */
  function fitFrame(entries) {
    const stageBody = document.querySelector('.stage-body');
    const frame = document.querySelector('.frame') || document.getElementById('frame');
    const strip = document.querySelector('.strip') || document.getElementById('info-strip');

    if (!frame) return;

    const isFullscreen = document.fullscreenElement === frame || 
      (document.fullscreenElement && frame.contains && frame.contains(document.fullscreenElement)) ||
      document.fullscreenElement != null;

    const viewport = document.getElementById('gameViewport');
    if (isFullscreen) {
      if (viewport) viewport.classList.remove('is-scaled');
      frame.style.width = '100vw';
      frame.style.height = '100vh';
      notifyGameResize();
      return;
    }

    if (!stageBody || !strip) return;
    const entry = Array.isArray(entries) && entries[0] ? entries[0] : null;
    const availW = entry ? entry.contentRect.width : stageBody.clientWidth;
    const availH = entry ? entry.contentRect.height : stageBody.clientHeight;
    if (!availW || !availH) return; // view hidden: skip

    let w = availW, h = (w * 9) / 16;
    if (h > availH) {
      h = availH;
      w = (h * 16) / 9;
    }
    const floorW = Math.floor(w);
    const floorH = Math.floor(h);

    requestAnimationFrame(() => {
      frame.style.width = floorW + 'px';
      frame.style.height = floorH + 'px';

      // The embedded game UI is laid out for desktop; on narrow frames render it at a
      // 960x540 design size and scale it down so menus fit instead of overflowing.
      if (viewport) {
        const EMBED_DESIGN_W = 960;
        const scaled = floorW < EMBED_DESIGN_W * 0.85;
        viewport.classList.toggle('is-scaled', scaled);
        viewport.style.setProperty('--embed-scale', scaled ? String(floorW / EMBED_DESIGN_W) : '1');
      }

      // The strip is never allowed to be narrower than 520px on desktop. If w is less than 520, set strip.style.width = '100%'.
      if (floorW < 520) {
        strip.style.width = '100%';
      } else {
        strip.style.width = floorW + 'px';
      }

      notifyGameResize();
    });
  }

  // fitFrame runs on 'resize' and also dispatches 'resize' so the game canvas re-measures.
  // Guard against re-entry, and only dispatch when the canvas is active.
  let notifyingResize = false;
  function notifyGameResize() {
    if (notifyingResize) return;
    const canvas = document.getElementById('game-canvas');
    if (!nightfallStarted || !canvas || canvas.offsetParent === null) return;
    notifyingResize = true;
    try {
      window.dispatchEvent(new Event('resize'));
    } finally {
      notifyingResize = false;
    }
  }

  function initTabNavigation() {
    // Nav tabs are ordinary links to real pages; this only positions the highlight pill.
    // Old in-page tab links (/#about, /#faq, /#guides) now live at their own URLs.
    const legacy = { about: '/about/', faq: '/faq/', guides: '/guides/' };
    const hash = window.location.hash.replace('#', '');
    if (window.location.pathname === '/' && legacy[hash]) {
      window.location.replace(legacy[hash]);
      return;
    }
    const pill = document.getElementById('tab-sliding-pill');
    if (!pill) return;
    const sync = (animate = true) => {
      const active = document.querySelector('.nav-tab-btn.is-active');
      if (!active) {
        pill.style.opacity = '0';
        return;
      }
      const left = active.offsetLeft;
      const width = active.offsetWidth;
      if (!animate) pill.style.transition = 'none';
      pill.style.opacity = '1';
      pill.style.left = `${left}px`;
      pill.style.width = `${width}px`;
      if (!animate) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            pill.style.transition = '';
          });
        });
      }
    };
    // Position the pill after layout stabilizes without triggering a synchronous forced reflow
    requestAnimationFrame(() => {
      requestAnimationFrame(() => sync(false));
    });
    window.addEventListener('load', () => requestAnimationFrame(() => sync(false)));
    window.addEventListener('resize', () => sync(true));
  }

  const ICON_PAUSE = `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>`;
  const ICON_PLAY = `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="5 3 19 12 5 21 5 3"/></svg>`;
  const ICON_SOUND = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>`;
  const ICON_MUTED = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>`;
  const ICON_FULLSCREEN = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>`;
  const ICON_EXIT_FULLSCREEN = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>`;

  function initGameStageAndHUD() {
    const pauseBtn = document.getElementById('hud-btn-pause');
    const resumeBtn = document.getElementById('btn-stage-resume');
    const restartBtn = document.getElementById('hud-btn-restart');
    const soundBtn = document.getElementById('hud-btn-sound');
    const fullscreenBtn = document.getElementById('hud-btn-fullscreen');
    const stageFrame = document.getElementById('frame') || document.getElementById('game-stage-frame');
    const stageBody = document.querySelector('.stage-body');
    const canvas = document.getElementById('game-canvas');

    if (stageBody) {
      new ResizeObserver(fitFrame).observe(stageBody);
    }

    const stageStartBtn = document.getElementById('stage-start-btn');
    if (stageStartBtn) {
      stageStartBtn.addEventListener('click', () => {
        loadGameIntoStage('nightfall');
      });
    }

    function updateFullscreenBtn() {
      if (!fullscreenBtn) return;
      const isFs = document.fullscreenElement != null;
      fullscreenBtn.innerHTML = isFs ? ICON_EXIT_FULLSCREEN : ICON_FULLSCREEN;
      fullscreenBtn.setAttribute('data-tooltip', isFs ? 'Exit Fullscreen' : 'Fullscreen');
      fullscreenBtn.setAttribute('aria-label', isFs ? 'Exit Fullscreen' : 'Fullscreen');
    }

    document.addEventListener('fullscreenchange', () => {
      fitFrame();
      updateFullscreenBtn();
    });
    document.addEventListener('webkitfullscreenchange', () => {
      fitFrame();
      updateFullscreenBtn();
    });

    if (pauseBtn) {
      pauseBtn.addEventListener('click', () => {
        if (isGamePaused) resumeGame();
        else pauseGame(true);
      });
    }

    if (resumeBtn) {
      resumeBtn.addEventListener('click', () => {
        resumeGame();
      });
    }

    if (restartBtn) {
      restartBtn.addEventListener('click', () => {
        restartCurrentGame();
      });
    }

    if (soundBtn) {
      soundBtn.addEventListener('click', () => {
        toggleSound(soundBtn);
      });
    }

    if (fullscreenBtn && stageFrame) {
      fullscreenBtn.addEventListener('click', () => {
        if (!document.fullscreenElement) {
          stageFrame.requestFullscreen().catch((err) => {
            console.warn('Fullscreen request failed:', err);
          });
        } else {
          document.exitFullscreen();
        }
      });
    }

    const headerFsBtn = document.getElementById('header-btn-fs');
    if (headerFsBtn) {
      headerFsBtn.addEventListener('click', () => {
        if (!document.fullscreenElement) {
          const target = stageFrame || document.documentElement;
          target.requestFullscreen().catch(() => {});
        } else {
          document.exitFullscreen();
        }
      });
    }

    if (canvas) {
      canvas.addEventListener('click', () => {
        canvas.focus({ preventScroll: true });
      });
    }

    // Listen to arcadia:gamestate to control top HUD and live game values
    window.addEventListener('arcadia:gamestate', (e) => {
      const detail = e.detail || {};
      const viewCollection = document.getElementById('view-collection');
      if (!viewCollection) return;

      if (detail.screen === 'play') {
        viewCollection.classList.remove('hud-top-hidden');
        if (detail.score !== undefined) {
          const scoreEl = document.getElementById('hud-score');
          if (scoreEl) scoreEl.textContent = String(detail.score).padStart(6, '0');
        }
        if (detail.wave !== undefined) {
          const comboEl = document.getElementById('hud-combo');
          if (comboEl) comboEl.textContent = `W${detail.wave}`;
        }
        if (detail.time !== undefined) {
          const timeEl = document.getElementById('hud-time');
          if (timeEl) {
            const mins = Math.floor(detail.time / 60);
            const secs = Math.floor(detail.time % 60);
            timeEl.textContent = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
          }
        }
      } else {
        viewCollection.classList.add('hud-top-hidden');
      }
    });
  }

  function pauseGame(showOverlay = true) {
    isGamePaused = true;
    const pauseOverlay = document.getElementById('stage-pause-overlay');
    const pauseBtn = document.getElementById('hud-btn-pause');
    if (pauseOverlay && showOverlay) {
      pauseOverlay.classList.add('is-active');
      pauseOverlay.inert = false;
    }
    if (pauseBtn) {
      pauseBtn.innerHTML = ICON_PLAY;
      pauseBtn.setAttribute('data-tooltip', 'Resume');
      pauseBtn.setAttribute('aria-label', 'Resume');
    }
    window.dispatchEvent(new CustomEvent('arcadia:pause-game'));
    const iframe = document.getElementById('stage-iframe');
    if (iframe && iframe.contentWindow) {
      try {
        iframe.contentWindow.postMessage({ type: 'arcadia:pause-game' }, '*');
      } catch (e) {}
    }
  }

  function resumeGame() {
    isGamePaused = false;
    const pauseOverlay = document.getElementById('stage-pause-overlay');
    const pauseBtn = document.getElementById('hud-btn-pause');
    if (pauseOverlay) {
      pauseOverlay.classList.remove('is-active');
      pauseOverlay.inert = true;
    }
    if (pauseBtn) {
      pauseBtn.innerHTML = ICON_PAUSE;
      pauseBtn.setAttribute('data-tooltip', 'Pause');
      pauseBtn.setAttribute('aria-label', 'Pause');
    }
    window.dispatchEvent(new CustomEvent('arcadia:resume-game'));
    const iframe = document.getElementById('stage-iframe');
    if (iframe && iframe.contentWindow) {
      try {
        iframe.contentWindow.postMessage({ type: 'arcadia:resume-game' }, '*');
      } catch (e) {}
    }
  }

  function restartCurrentGame() {
    const canvas = document.getElementById('game-canvas');
    if (currentGameId === 'nightfall') {
      window.dispatchEvent(new CustomEvent('arcadia:restart-game'));
      if (canvas) canvas.focus({ preventScroll: true });
    } else {
      const targetId = currentGameId;
      teardownCurrentGame(targetId);
      const iframe = document.getElementById('stage-iframe');
      if (iframe) {
        iframe.classList.remove('is-hidden');
        iframe.src = `/playable/${targetId}/index.html`;
      }
    }
    resumeGame();
  }

  let activeAudioStateMuted = false;
  function toggleSound(btn) {
    const isMuted = btn.getAttribute('aria-pressed') === 'false';
    const nextMuted = !isMuted;
    activeAudioStateMuted = nextMuted;
    btn.setAttribute('aria-pressed', nextMuted ? 'false' : 'true');
    btn.setAttribute('data-tooltip', nextMuted ? 'Unmute' : 'Mute');
    btn.setAttribute('aria-label', nextMuted ? 'Unmute' : 'Mute');
    btn.innerHTML = nextMuted ? ICON_MUTED : ICON_SOUND;
    btn.style.opacity = nextMuted ? '0.55' : '1';
    window.dispatchEvent(new CustomEvent('arcadia:toggle-sound', { detail: { muted: nextMuted } }));
    const iframe = document.getElementById('stage-iframe');
    if (iframe && iframe.contentWindow) {
      try {
        iframe.contentWindow.postMessage({ type: 'arcadia:toggle-sound', muted: nextMuted }, '*');
      } catch (e) {}
    }
  }

  /* ==========================================================================
     4. Library Game Switching & Inline Drawers (Section 3.2 & 7.3)
     ========================================================================== */
  function initLibraryCards() {
    const cards = document.querySelectorAll('.lib-game-card');
    const searchInput = document.getElementById('library-search-input');
    const filterBtns = document.querySelectorAll('.library-filter-btn');
    const countBadge = document.getElementById('library-count');

    // Play button & Card Details Drawer
    cards.forEach((card) => {
      const playBtn = card.querySelector('[data-play-btn]');
      const detailsBtn = card.querySelector('[data-details-toggle]');
      const drawer = card.querySelector('.lib-details-drawer');
      const gameId = card.getAttribute('data-game-id');

      if (playBtn) {
        playBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          loadGameIntoStage(gameId, card);
        });
      }

      if (detailsBtn && drawer) {
        detailsBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          const isOpen = drawer.classList.contains('is-open');
          drawer.classList.toggle('is-open', !isOpen);
          detailsBtn.textContent = isOpen ? 'Details' : 'Less';
          detailsBtn.setAttribute('aria-expanded', !isOpen ? 'true' : 'false');
        });
      }
    });

    // Library search + genre filter apply together
    let libFilter = 'all';
    function filterLibrary() {
      const query = (searchInput?.value || '').toLowerCase().trim();
      let visibleCount = 0;
      cards.forEach((card) => {
        const name = (card.getAttribute('data-name') || '').toLowerCase();
        const genre = (card.getAttribute('data-genre') || '').toLowerCase();
        const cats = (card.getAttribute('data-categories') || '').split(' ');
        const match = (libFilter === 'all' || cats.includes(libFilter)) &&
          (!query || name.includes(query) || genre.includes(query));
        card.style.display = match ? '' : 'none';
        if (match) visibleCount++;
      });
      if (countBadge) {
        countBadge.textContent = `${visibleCount} ${visibleCount === 1 ? 'game' : 'games'}`;
      }
    }

    if (searchInput) searchInput.addEventListener('input', filterLibrary);

    filterBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        filterBtns.forEach((b) => {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        libFilter = btn.getAttribute('data-lib-filter') || 'all';
        filterLibrary();
      });
    });
  }

  function initCatalogAndShelves() {
    // 1. Play buttons across all shelves and catalog cards (library cards handled in initLibraryCards)
    const playBtns = document.querySelectorAll('.catalog-game-card [data-play-btn], .shelf-card [data-play-btn]');
    playBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const gameId = btn.getAttribute('data-play-btn');
        if (!gameId) return;

        loadGameIntoStage(gameId);

        // Smooth scroll up to stage if clicked from catalog below
        const viewCollection = document.getElementById('view-collection');
        if (viewCollection) {
          viewCollection.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    });

    // 2. Shelf navigation arrows
    const prevBtn = document.getElementById('shelf-prev-btn');
    const nextBtn = document.getElementById('shelf-next-btn');
    const shelfTrack = document.getElementById('shelf-scroll-track');
    if (shelfTrack) {
      if (prevBtn) {
        prevBtn.addEventListener('click', () => {
          shelfTrack.scrollBy({ left: -320, behavior: 'smooth' });
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', () => {
          shelfTrack.scrollBy({ left: 320, behavior: 'smooth' });
        });
      }
    }

    // 3. Jump to catalog button
    const jumpBtn = document.getElementById('btn-jump-catalog');
    const catalogSection = document.getElementById('catalog-section');
    if (jumpBtn && catalogSection) {
      jumpBtn.addEventListener('click', (e) => {
        e.preventDefault();
        catalogSection.scrollIntoView({ behavior: 'smooth' });
      });
    }

    // 4. Catalog Category Filtering & Real-Time Search
    const catChips = document.querySelectorAll('.cat-chip');
    const searchInput = document.getElementById('catalog-search-input');
    const countPill = document.getElementById('catalog-results-count');
    const emptyState = document.getElementById('catalog-empty-state');
    const resetBtn = document.getElementById('btn-reset-filters');
    const catalogCards = document.querySelectorAll('.catalog-game-card');

    let currentCat = 'all';

    function filterCatalog() {
      const query = (searchInput?.value || '').toLowerCase().trim();
      let visibleCount = 0;

      catalogCards.forEach((card) => {
        const cats = (card.getAttribute('data-categories') || '').toLowerCase().split(' ');
        const name = (card.getAttribute('data-name') || '').toLowerCase();
        const genre = (card.getAttribute('data-genre') || '').toLowerCase();
        const text = (card.textContent || '').toLowerCase();

        const matchCat = currentCat === 'all' || cats.includes(currentCat);
        const matchQuery = !query || name.includes(query) || genre.includes(query) || text.includes(query);

        const isVisible = matchCat && matchQuery;
        card.style.display = isVisible ? '' : 'none';
        if (isVisible) visibleCount++;
      });

      if (countPill) {
        countPill.textContent = `${visibleCount} ${visibleCount === 1 ? 'game' : 'games'}`;
      }
      if (emptyState) {
        emptyState.classList.toggle('is-hidden', visibleCount > 0);
      }
    }

    catChips.forEach((chip) => {
      chip.addEventListener('click', () => {
        catChips.forEach((c) => {
          c.classList.remove('is-active');
          c.setAttribute('aria-pressed', 'false');
        });
        chip.classList.add('is-active');
        chip.setAttribute('aria-pressed', 'true');
        currentCat = chip.getAttribute('data-cat-filter') || 'all';
        filterCatalog();
      });
    });

    if (searchInput) {
      searchInput.addEventListener('input', filterCatalog);
    }

    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        if (searchInput) searchInput.value = '';
        currentCat = 'all';
        catChips.forEach((c) => {
          const isAll = c.getAttribute('data-cat-filter') === 'all';
          c.classList.toggle('is-active', isAll);
          c.setAttribute('aria-pressed', isAll ? 'true' : 'false');
        });
        filterCatalog();
      });
    }
  }

  let splashTransitionTimer = null;

  function teardownCurrentGame(prevId) {
    if (splashTransitionTimer) {
      clearTimeout(splashTransitionTimer);
      splashTransitionTimer = null;
    }

    // 1. Immediately silence & halt Nightfall if it was running on the host canvas
    window.dispatchEvent(new CustomEvent('arcadia:stop-game'));
    const canvasStage = document.getElementById('play');
    if (canvasStage) {
      canvasStage.style.display = 'none';
    }

    // 2. Completely terminate & unload any running iframe game
    const iframe = document.getElementById('stage-iframe');
    if (iframe) {
      try {
        if (iframe.contentWindow) {
          const win = iframe.contentWindow;
          try {
            win.postMessage({ type: 'arcadia:teardown' }, '*');
            win.dispatchEvent(new Event('pagehide'));
            win.dispatchEvent(new Event('unload'));
          } catch (e) {}

          const doc = win.document;
          if (doc) {
            const mediaList = doc.querySelectorAll('audio, video');
            mediaList.forEach((media) => {
              try {
                media.pause();
                media.muted = true;
                media.currentTime = 0;
                media.src = '';
                media.load();
              } catch (e) {}
            });
          }

          if (win.AudioManager) {
            try { if (typeof win.AudioManager.mute === 'function') win.AudioManager.mute(); } catch (e) {}
            try { if (typeof win.AudioManager.stop === 'function') win.AudioManager.stop(); } catch (e) {}
            try { if (typeof win.AudioManager.stopMusic === 'function') win.AudioManager.stopMusic(); } catch (e) {}
            try { if (win.AudioManager.ctx && typeof win.AudioManager.ctx.close === 'function') win.AudioManager.ctx.close(); } catch (e) {}
          }
        }
      } catch (e) {
        // Cross-window fallback
      }

      // Explicitly blank out src immediately so audio and network stop right away
      try {
        iframe.src = 'about:blank';
      } catch (e) {}

      // Replace with clean new iframe node to ensure complete destruction of internal state & audio contexts
      try {
        const parent = iframe.parentNode;
        if (parent) {
          const freshIframe = document.createElement('iframe');
          freshIframe.id = 'stage-iframe';
          freshIframe.className = 'stage-iframe is-hidden';
          freshIframe.title = 'Game';
          freshIframe.allow = 'gamepad; fullscreen; autoplay; accelerometer; clipboard-write';
          parent.replaceChild(freshIframe, iframe);
        }
      } catch (e) {
        iframe.classList.add('is-hidden');
      }
    }
  }

  function loadGameIntoStage(gameId, targetCard) {
    hideStageStart();

    // If already playing this exact game and active, keep focus
    if (currentGameId === gameId && !isGamePaused) {
      if (gameId === 'nightfall') {
        // NIGHTFALL is the default stage game but its engine loads on first Play.
        if (!nightfallStarted) startNightfall();
        const canvas = document.getElementById('game-canvas');
        if (canvas) canvas.focus({ preventScroll: true });
      } else {
        const iframe = document.getElementById('stage-iframe');
        if (iframe) iframe.focus();
      }
      return;
    }

    const prevGameId = currentGameId;
    currentGameId = gameId;

    // Immediately stop & close the previous game right now (cuts off all sound instantly)
    teardownCurrentGame(prevGameId);

    const allCards = document.querySelectorAll('.lib-game-card, .catalog-game-card, .shelf-card');
    const splash = document.getElementById('stage-loading-splash');
    const splashTitle = document.getElementById('splash-title');
    const splashMeta = document.getElementById('splash-meta');
    const splashArt = document.getElementById('splash-bg-art');
    const splashBar = document.getElementById('splash-progress-bar');
    const canvasStage = document.getElementById('play');
    const hudTitle = document.getElementById('hud-game-title');
    const hudGenre = document.getElementById('hud-game-genre');
    const hudScore = document.getElementById('hud-score');
    const infoGenre = document.getElementById('info-genre-tag');

    const card = targetCard || document.querySelector(`[data-game-id="${gameId}"]`);
    const gameName = card ? card.getAttribute('data-name') : gameId.toUpperCase();
    const gameGenre = card ? card.getAttribute('data-genre') : 'Arcade';
    const gameThumb = card ? card.getAttribute('data-thumb') : '/games/thumbnails/nightfall.jpg';

    // 1. Update Card States Across Library, Shelves, and Grid
    allCards.forEach((c) => {
      c.classList.remove('is-now-playing', 'shimmer-active');
      const tag = c.querySelector('.lib-now-playing-tag');
      if (tag) tag.remove();
      const pBtn = c.querySelector('[data-play-btn]');
      if (pBtn) {
        pBtn.classList.remove('is-playing-btn');
        pBtn.removeAttribute('aria-label');
        const span = pBtn.querySelector('span');
        if (span) span.textContent = 'Play';
      }
    });

    const activeCards = document.querySelectorAll(`[data-game-id="${gameId}"]`);
    activeCards.forEach((c) => {
      c.classList.add('is-now-playing', 'shimmer-active');
      const media = c.querySelector('.lib-card-media');
      if (media && !media.querySelector('.lib-now-playing-tag')) {
        const badge = document.createElement('span');
        badge.className = 'lib-now-playing-tag';
        badge.textContent = 'NOW PLAYING';
        media.appendChild(badge);
      }
      const pBtn = c.querySelector('[data-play-btn]');
      if (pBtn) {
        pBtn.classList.add('is-playing-btn');
        pBtn.removeAttribute('aria-label');
        const span = pBtn.querySelector('span');
        if (span) span.textContent = 'Playing';
      }
    });

    // 2. Show Loading Splash (~0.9s duration)
    if (splash) {
      if (splashTitle) splashTitle.textContent = gameName;
      if (splashMeta) splashMeta.textContent = `${gameGenre} · Ready · 60 FPS`;
      if (splashArt) splashArt.style.backgroundImage = `url('${gameThumb}')`;
      if (splashBar) {
        splashBar.style.width = '0%';
      }

      splash.classList.add('is-active');
      setTimeout(() => {
        if (splashBar) splashBar.style.width = '100%';
      }, 50);

      const delay = isReducedMotion ? 10 : 920;
      splashTransitionTimer = setTimeout(() => {
        splashTransitionTimer = null;

        // Switch Engine / Viewport
        if (gameId === 'nightfall') {
          if (canvasStage) canvasStage.style.display = 'block';
          const freshIframe = document.getElementById('stage-iframe');
          if (freshIframe) freshIframe.classList.add('is-hidden');
          startNightfall();
          window.dispatchEvent(new CustomEvent('arcadia:resume-game'));
          const canvas = document.getElementById('game-canvas');
          if (canvas) canvas.focus({ preventScroll: true });
        } else if (gameId === 'nebula-drift') {
          window.location.href = '/games/nebula-drift/play/';
        } else {
          if (canvasStage) canvasStage.style.display = 'none';
          const freshIframe = document.getElementById('stage-iframe');
          if (freshIframe) {
            freshIframe.classList.remove('is-hidden');
            freshIframe.onload = () => {
              try {
                freshIframe.focus();
                if (activeAudioStateMuted && freshIframe.contentWindow) {
                  freshIframe.contentWindow.postMessage({ type: 'arcadia:toggle-sound', muted: true }, '*');
                }
              } catch (e) {}
            };
            freshIframe.src = `/playable/${gameId}/index.html`;
          }
        }

        // Cross-fade HUD values
        if (hudTitle) {
          hudTitle.style.opacity = '0';
          hudTitle.style.transform = 'translateY(-4px)';
          setTimeout(() => {
            hudTitle.textContent = gameName;
            hudTitle.style.transition = 'all 0.3s ease-out';
            hudTitle.style.opacity = '1';
            hudTitle.style.transform = 'translateY(0)';
          }, 120);
        }

        if (hudGenre) hudGenre.textContent = `${gameGenre} · Active`;
        if (infoGenre) infoGenre.textContent = gameGenre;

        // Score Pop Animation
        if (hudScore) {
          hudScore.classList.add('pop-anim');
          setTimeout(() => hudScore.classList.remove('pop-anim'), 300);
        }

        splash.classList.remove('is-active');
        resumeGame();
      }, delay);
    }
  }

  /* ==========================================================================
     5. FAQ Accordion & Search (Section 5 & 7.5)
     ========================================================================== */
  function initFaqAccordionAndSearch() {
    const faqItems = document.querySelectorAll('.faq-card-item');
    const searchInput = document.getElementById('faq-search-input');
    const chips = document.querySelectorAll('[data-faq-filter]');
    const emptyState = document.getElementById('faq-empty-state');

    // Accordion Toggle: only rotate the + icon 45deg, drawer uses grid-template-rows
    faqItems.forEach((item) => {
      const trigger = item.querySelector('.faq-card-trigger');
      if (trigger) {
        trigger.addEventListener('click', () => {
          const isOpen = item.classList.contains('is-open');
          item.classList.toggle('is-open', !isOpen);
          trigger.setAttribute('aria-expanded', !isOpen ? 'true' : 'false');
        });
      }
    });

    // Category Filter Chips
    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        chips.forEach((c) => {
          c.classList.remove('is-active');
          c.setAttribute('aria-pressed', 'false');
        });
        chip.classList.add('is-active');
        chip.setAttribute('aria-pressed', 'true');
        filterFaqList();
      });
    });

    if (searchInput) {
      searchInput.addEventListener('input', () => {
        filterFaqList();
      });
    }

    function filterFaqList() {
      const activeChip = document.querySelector('[data-faq-filter].is-active');
      const selectedCategory = activeChip ? activeChip.getAttribute('data-faq-filter') : 'all';
      const query = (searchInput ? searchInput.value : '').toLowerCase().trim();
      let matchCount = 0;

      faqItems.forEach((item) => {
        const itemCat = item.getAttribute('data-faq-cat') || '';
        const itemText = item.innerText.toLowerCase();

        const catMatches = selectedCategory === 'all' || itemCat === selectedCategory;
        const queryMatches = !query || itemText.includes(query);

        if (catMatches && queryMatches) {
          item.style.display = '';
          if (!isReducedMotion) {
            item.style.animation = `item-cascade-in 0.38s cubic-bezier(0.16, 1, 0.3, 1) ${matchCount * 0.04}s both`;
          }
          matchCount++;
        } else {
          item.style.display = 'none';
        }
      });

      if (emptyState) {
        emptyState.classList.toggle('is-hidden', matchCount > 0);
      }
    }
  }

  /* ==========================================================================
     8. 3D Tilt & Mouse Spotlight on Game Cards (Section 7.5)
     ========================================================================== */
  function initCard3DTilt() {
    if (isReducedMotion || window.matchMedia('(hover: none)').matches) return;
    const cards = document.querySelectorAll(
      '.lib-game-card, .value-card, .genre-showcase-card, .featured-guide-card, .guide-item-card, .shelf-card, .catalog-game-card, .strip-item, .faq-card-item, .seo-prose-block'
    );

    cards.forEach((card) => {
      let rect = null;
      let rafId = null;

      card.addEventListener('mouseenter', () => {
        rect = card.getBoundingClientRect();
      });

      card.addEventListener('mousemove', (e) => {
        if (!rect) rect = card.getBoundingClientRect();
        const clientX = e.clientX;
        const clientY = e.clientY;

        if (rafId) return;
        rafId = requestAnimationFrame(() => {
          rafId = null;
          if (!rect) return;
          const x = clientX - rect.left;
          const y = clientY - rect.top;
          const centerX = rect.width / 2;
          const centerY = rect.height / 2;

          const rotateX = ((y - centerY) / centerY) * -3.5;
          const rotateY = ((x - centerX) / centerX) * 3.5;

          card.style.setProperty('--mouse-x', `${((x / rect.width) * 100).toFixed(1)}%`);
          card.style.setProperty('--mouse-y', `${((y / rect.height) * 100).toFixed(1)}%`);
          card.style.transform = `perspective(800px) rotateX(${rotateX.toFixed(2)}deg) rotateY(${rotateY.toFixed(2)}deg) translateY(-3px)`;
        });
      });

      card.addEventListener('mouseleave', () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        rect = null;
        card.style.transform = '';
      });
    });
  }

  /* ==========================================================================
     9. Global Keyboard Shortcuts & Game Auto-Focus
     ========================================================================== */
  function initKeyboardShortcuts() {
    window.addEventListener('keydown', (e) => {
      const isInput = e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA');

      // 'Escape' to blur search/filter inputs and restore general focus
      if (e.key === 'Escape' && isInput) {
        e.target.blur();
        return;
      }

      // '/' to focus Search
      if (e.key === '/' && !isInput) {
        e.preventDefault();
        const activeView = document.querySelector('.hub-view.is-active');
        if (activeView && activeView.id === 'view-faq') {
          const faqInput = document.getElementById('faq-search-input');
          if (faqInput) faqInput.focus();
        } else {
          const libInput = document.getElementById('library-search-input');
          if (libInput) libInput.focus();
        }
        return;
      }

      // Prevent Arrow keys from scrolling the webpage during gameplay
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key) && !isInput) {
        const stage = document.getElementById('frame') || document.getElementById('game-stage-frame');
        const canvas = document.getElementById('game-canvas');
        const isFocused = document.activeElement === canvas || (stage && stage.contains(document.activeElement));
        const isHovered = stage && stage.matches(':hover');
        if (isFocused || isHovered) {
          e.preventDefault();
        }
      }
    });
  }

  /* ==========================================================================
     10. Standalone Play Page Controls & Quick Switcher
     ========================================================================== */
  function initStandalonePlayPage() {
    const playContainer = document.querySelector('.play-page-container');
    if (!playContainer) return;

    const fullscreenBtn = document.getElementById('btn-play-fullscreen');
    const restartBtn = document.getElementById('btn-play-restart');
    const viewport = document.getElementById('game-viewport');
    const iframe = document.getElementById('standalone-play-iframe');
    const canvas = document.getElementById('game-canvas');
    const switchSearch = document.getElementById('switch-search-input');
    const switchItems = document.querySelectorAll('.switch-game-item');
    const startBtn = document.getElementById('play-start-btn');

    // The game only loads when the player asks for it: keeps the page fast and
    // gives browsers the user gesture they require before games can play audio.
    function startPlay() {
      hideStageStart();
      if (iframe) {
        if (!iframe.getAttribute('src')) iframe.src = iframe.dataset.src;
        iframe.focus();
      } else if (document.getElementById('nd-root')) {
        const ndRoot = document.getElementById('nd-root');
        if (ndRoot) ndRoot.focus?.({ preventScroll: true });
      } else {
        startNightfall();
        if (canvas) canvas.focus({ preventScroll: true });
      }
    }
    if (startBtn) startBtn.addEventListener('click', startPlay);

    if (fullscreenBtn && viewport) {
      function updateFsLabel() {
        const isFs = document.fullscreenElement != null;
        const label = fullscreenBtn.querySelector('.util-label');
        if (label) label.textContent = isFs ? 'Exit Fullscreen' : 'Fullscreen';
      }

      fullscreenBtn.addEventListener('click', () => {
        if (!document.fullscreenElement) {
          viewport.requestFullscreen().catch((err) => {
            console.warn('Fullscreen request failed:', err);
          });
        } else {
          document.exitFullscreen();
        }
      });

      document.addEventListener('fullscreenchange', updateFsLabel);
      document.addEventListener('webkitfullscreenchange', updateFsLabel);
    }

    if (restartBtn) {
      restartBtn.addEventListener('click', () => {
        if (startBtn && !startBtn.closest('.stage-start').hidden) {
          startPlay();
          return;
        }
        if (iframe && iframe.src) {
          const src = iframe.src;
          iframe.src = '';
          setTimeout(() => {
            iframe.src = src;
          }, 50);
        } else if (canvas) {
          window.dispatchEvent(new CustomEvent('arcadia:restart-game'));
          canvas.focus({ preventScroll: true });
        }
      });
    }

    if (switchSearch && switchItems.length) {
      switchSearch.addEventListener('input', () => {
        const query = switchSearch.value.toLowerCase().trim();
        switchItems.forEach((item) => {
          const title = (item.querySelector('.switch-item-title')?.textContent || '').toLowerCase();
          const genre = (item.querySelector('.switch-item-genre')?.textContent || '').toLowerCase();
          const match = !query || title.includes(query) || genre.includes(query);
          item.style.display = match ? '' : 'none';
        });
      });
    }

    // Key shortcuts 'f' for fullscreen, 'r' for restart on standalone play page
    window.addEventListener('keydown', (e) => {
      const isInput = e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA');
      if ((e.key === 'f' || e.key === 'F') && !isInput && viewport) {
        if (!document.fullscreenElement) {
          viewport.requestFullscreen().catch(() => {});
        } else {
          document.exitFullscreen();
        }
      }
      if ((e.key === 'r' || e.key === 'R') && !isInput && restartBtn) {
        restartBtn.click();
      }
    });
  }
})();

